import { useEffect, useState } from "react";
import { ScanLine, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Panel, Spinner, Tag } from "@/components/common";

const ICON = { ok: [CheckCircle2, "text-emerald-400"], warn: [AlertTriangle, "text-amber-300"], fail: [XCircle, "text-red-400"] };

const Row = ({ i }) => {
  const [Icon, cls] = ICON[i.status];
  return (
    <li data-testid={`tracking-item-${i.key}`} className="flex gap-3 border-b border-[#1e293b] py-2.5 last:border-0">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${cls}`} />
      <div className="min-w-0">
        <div className="text-sm text-slate-200">{i.title}</div>
        {i.detail && <div className="mt-0.5 break-words text-xs text-slate-500">{i.detail}</div>}
        {i.fix && <div className="mt-1 text-xs text-emerald-300">Fix: {i.fix}</div>}
      </div>
    </li>
  );
};

const Actions = ({ actions }) => (
  <details className="mt-3 text-xs" data-testid="tracking-actions">
    <summary className="cursor-pointer text-slate-400">All Google Ads conversion actions ({actions.length})</summary>
    <div className="mt-2 overflow-x-auto"><table className="w-full text-left font-mono text-[11px]"><thead><tr className="text-slate-500">{["Action", "Status", "Category", "Goal", "Counting", "7d", "14d", "30d", "Last conv."].map((h) => <th key={h} className="pb-1 pr-2 font-normal">{h}</th>)}</tr></thead>
      <tbody>{actions.map((a) => (
        <tr key={a.id} className="border-t border-[#1e293b] text-slate-300"><td className="py-1 pr-2">{a.name}</td><td className="pr-2">{a.status}</td><td className="pr-2">{a.category}</td>
          <td className="pr-2">{a.primary && a.in_conversions ? "Primary" : "Secondary"}</td><td className="pr-2">{a.counting.replace("_PER_CLICK", "")}</td>
          <td className="pr-2">{a.d7}</td><td className="pr-2">{a.d14}</td><td className="pr-2">{a.d30}</td><td>{a.last_conversion || "—"}</td></tr>
      ))}</tbody></table></div>
  </details>
);

export const TrackingCheck = ({ testid = "tracking-check" }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/tracking/check").then((r) => setC(r.data.check)); }, []);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/tracking/check"); setC(data.check); data.cached ? toast.info(data.message) : toast.success("Tracking checked"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Conversion tracking check" icon={ScanLine} testid={testid}
      right={<button data-testid={`${testid}-run`} onClick={run} disabled={busy} className="cc-btn cc-btn-primary px-3 py-1.5 text-xs">{busy ? <Spinner className="h-3.5 w-3.5" /> : <ScanLine className="h-3.5 w-3.5" />} Check tracking</button>}>
      {!c ? <p className="text-sm text-slate-400">Not checked yet. It also runs daily with the alerts job and emails you when something breaks.</p> : (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
            <Tag tone="emerald">✅ {c.summary.ok}</Tag><Tag tone="amber">⚠️ {c.summary.warn}</Tag><Tag tone="red">❌ {c.summary.fail}</Tag>
            <span className="font-mono text-slate-500" data-testid={`${testid}-last`}>Last checked {fmtDate(c.at)}</span>
          </div>
          <ul>{[...c.items].sort((a, b) => ["fail", "warn", "ok"].indexOf(a.status) - ["fail", "warn", "ok"].indexOf(b.status)).map((i) => <Row key={i.key} i={i} />)}</ul>
          <div className="mt-3 text-xs" data-testid="tracking-comparison"><div className="cc-eyebrow mb-1">App vs Google · last 14 days</div>
            {c.comparison.map((r) => <div key={r.event} className="flex justify-between font-mono text-slate-400"><span>{r.event}</span><span>app {r.app_14d} · Google {r.google_14d ?? "no action yet"}</span></div>)}</div>
          <Actions actions={c.actions} />
        </>
      )}
    </Panel>
  );
};
